"use client";

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { imagePath } from "@/lib/userUtils";
import { Enum_Comment_Commentstatus } from "@/types/generated";
import { zodResolver } from "@hookform/resolvers/zod";
import { Loader2 } from "lucide-react";
import { useForm } from "react-hook-form";
import { z } from "zod";

export const formSchema = z.object({
  content: z.string().min(1, "Comment cannot be empty"),
  article: z.string(),
  parentComment: z.string().optional(),
});

export interface CommentAuthor {
  username: string;
  documentId?: string;
  name?: string;
  slug?: string;
  avatar?: {
    documentId?: string;
    url?: string;
  };
  role?:{
    documentId?: string
    name?: string
    type?: string
  }
}

export interface Comment {
  documentId: string;
  content: string;
  approvedBy: CommentAuthor;
  commentBy: CommentAuthor;
  createdAt: string;
  updatedAt: string;
  commentStatus: Enum_Comment_Commentstatus;
  approved?: boolean;
  parentComment?: {
    documentId: string;
  };
  replies?: Comment[] | undefined;
}

export interface CommentSectionProps {
  article: {
    documentId: string;
  };
  initialComments?: Comment[];
}

// Comment Form Component
export interface CommentFormProps {
  articleId: string;
  parentCommentId?: string;
  user: any;
  token: string | null;
  onSubmit: (data: z.infer<typeof formSchema>) => Promise<void>;
  onCancel?: () => void;
  isReply?: boolean;
}

export function CommentForm({
  articleId,
  parentCommentId,
  user,
  token,
  onSubmit,
  onCancel,
  isReply = false,
}: CommentFormProps) {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      article: articleId,
      parentComment: parentCommentId,
    },
  });

  const handleFormSubmit = async (values: z.infer<typeof formSchema>) => {
    await onSubmit(values);
    if (!isReply) reset();
  };

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-4">
      <div className="flex gap-4 mt-6">
        <Avatar className="h-10 w-10">
          <AvatarImage src={imagePath(user?.avatar?.url)} />
          <AvatarFallback>
            {user?.username?.charAt(0).toUpperCase() || "U"}
          </AvatarFallback>
        </Avatar>
        <div className="flex-1 space-y-2 pr-2">
          <Textarea
            {...register("content")}
            placeholder={
              isReply ? "Write your reply..." : "Write your comment..."
            }
            className="min-h-[60px] resize-none"
            disabled={isSubmitting}
            autoFocus={isReply}
          />
          {errors.content && (
            <p className="text-sm text-red-500">{errors.content.message}</p>
          )}
          <div className="flex justify-end gap-2">
            {isReply && onCancel && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={onCancel}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
            )}
            <Button
              size="sm"
              type="submit"
              disabled={isSubmitting}
              className="px-6"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Posting...
                </>
              ) : isReply ? (
                "Reply"
              ) : (
                "Comment"
              )}
            </Button>
          </div>
        </div>
      </div>
    </form>
  );
}
